/**
 * PracticeAgent — handles "create a practice plan" requests and the
 * pending follow-up where the user supplies a focus area for an existing
 * placeholder practice.
 *
 * Phase A extracts the placeholder/plan-generation logic from
 * `CoachSteve.tsx` so it lives next to its sibling agents. The chat
 * component still owns the React state setters, so each handler accepts
 * an `AgentRunContext` to keep the UX 1:1 with the previous behavior.
 */

import { toast } from 'react-hot-toast';
import { generateExpandedDrillForPractice, generatePracticePlan } from '../../../services/aiService.simplified';
import { PracticeService } from '../../../services/database';
import {
  computeDrillInsertVisualIndex,
  insertPracticeDrillAtOrderSlot,
  listDrillsOrderMeta,
  saveDrills,
} from '../../../services/practice/practiceService';
import type { Drill, Practice } from '../../../types';
import type { PracticePlanRequest } from '../../../services/aiService.types';
import type { Team } from '../../../types/core';
import type { CreatedPracticeInfo } from '../CoachStevePracticeForm';
import {
  extractPracticeActivityBrief,
  extractPracticeFocusFromMessage,
  getTomorrowDateStr,
  isAffirmativeReply,
  isNegativeReply,
  parsePracticeTargetDateFromMessage,
} from './parsers';
import { isAddDrillToPracticeIntent, isCreatePracticeIntent } from './intents';
import type { AgentRunContext, AgentRunResult, CoachSteveAgent } from './types';

function inferActivityDurationMinutes(phraseOrMessage: string, fallback: number = 12): number {
  const m = phraseOrMessage.match(/\b(?:last|final\s+)?(\d{1,2})\s*(?:min|minutes?)\b/i);
  if (!m) return fallback;
  return Math.min(45, Math.max(5, parseInt(m[1], 10)));
}

function formatPracticeDateLabel(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

async function findFirstPracticeOnDate(teamId: string, iso: string): Promise<Practice | null> {
  const all = await PracticeService.getPractices(teamId);
  const same = all
    .filter((p) => p.date === iso)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  return same[0] ?? null;
}

type RawAiDrill = {
  name: string;
  duration: number;
  description?: string;
  focus_area?: string;
  focus_areas?: string[];
  intensity_level?: Drill['intensity_level'];
  position_groups?: unknown[];
};

function drillsFromAiShape(practiceId: string, items: RawAiDrill[]): Omit<Drill, 'id'>[] {
  return (items || []).map((d) => ({
    name: d.name,
    duration: d.duration,
    description: d.description || '',
    focus_areas: d.focus_area ? [d.focus_area] : d.focus_areas || ['Team'],
    intensity_level: d.intensity_level,
    position_groups: (d.position_groups || []).map((pg: unknown) => {
      const obj = typeof pg === 'string' ? { group_name: pg, steps: [] } : (pg as Record<string, unknown>);
      const steps = (
        typeof obj === 'object' && Array.isArray((obj as { steps?: unknown[] }).steps)
          ? (obj as { steps: Record<string, unknown>[] }).steps
          : []
      ).map((s) => ({
        ...s,
        coaching_points: s.coachingPoints || s.coaching_points || [],
      }));
      return { ...obj, steps };
    }),
    practice_id: practiceId,
  })) as Omit<Drill, 'id'>[];
}

/** Create a placeholder practice using the team's default time/location. */
export async function createPlaceholderPractice(team: Team, dateISO?: string): Promise<CreatedPracticeInfo | null> {
  const date = dateISO?.trim() || getTomorrowDateStr();
  const startTime = team.defaultPracticeStartTime || '17:00';
  const endTime = team.defaultPracticeEndTime || '18:30';
  const location = team.defaultPracticeLocation || 'Field';
  const theme = 'Team Day';
  const nowIso = new Date().toISOString();
  const practiceData = {
    team_id: team.id,
    title: `Practice - ${theme}`,
    date,
    start_time: startTime,
    end_time: endTime,
    location,
    theme,
    focus_areas: [],
    drills: [],
    created_at: nowIso,
    updated_at: nowIso,
  };
  const newPractice = await PracticeService.createPractice(practiceData);
  if (!newPractice) return null;
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let durationMinutes = endH * 60 + endM - (startH * 60 + startM);
  if (durationMinutes <= 0) durationMinutes += 24 * 60;
  return {
    practiceId: newPractice.id,
    date,
    startTime,
    endTime,
    location,
    theme,
    team,
    durationMinutes,
  };
}

/** Generate the AI practice plan and persist drills to the placeholder. */
export async function runPlanGeneration(
  info: CreatedPracticeInfo,
  focusText: string,
  onProgressUpdate: (message: string) => void,
  options?: { coachMandatoryDrills?: string[] }
) {
  const aiRequest: PracticePlanRequest = {
    team: info.team,
    duration: info.durationMinutes,
    theme: info.theme as PracticePlanRequest['theme'],
    focusAreas: [focusText],
    coachMandatoryDrills: options?.coachMandatoryDrills,
    date: info.date,
    startTime: info.startTime,
    location: info.location,
    ageLevel: info.team.ageLevel,
    experienceLevel: info.team.experienceLevel,
  };
  let totalDrills = 0;
  let completedCount = 0;
  const plan = await generatePracticePlan(aiRequest, (progress) => {
    if (progress.status === 'count-update' && progress.totalDrills) {
      totalDrills = progress.totalDrills;
      onProgressUpdate(`Preparing your practice plan (${totalDrills} drills)…`);
    } else if (progress.status === 'generating' && progress.drillName) {
      const n = totalDrills ? completedCount + 1 : 0;
      const suffix = totalDrills ? ` (${n}/${totalDrills})` : '';
      onProgressUpdate(`Preparing: **${progress.drillName}**${suffix}`);
    } else if (progress.status === 'completed' && progress.drillName) {
      completedCount += 1;
      const suffix = totalDrills ? ` (${completedCount}/${totalDrills})` : '';
      onProgressUpdate(`Prepared: ${progress.drillName}${suffix}`);
    }
  });

  // The aiService returns generated drills with a slightly different shape
  // (snake_case + nested coaching_points/coachingPoints). Normalize before
  // persisting so the practice writer always gets consistent rows.
  const collectedDrills: Omit<Drill, 'id'>[] = drillsFromAiShape(
    info.practiceId,
    (plan.drills || []) as RawAiDrill[]
  );

  if (collectedDrills.length > 0) {
    await saveDrills(info.practiceId, collectedDrills as Drill[]);
  }
  return plan;
}

/**
 * Show the practice "what should we focus on?" follow-up message and
 * stash the placeholder so subsequent user input runs `runPlanGeneration`.
 */
export function handlePracticeCreated(info: CreatedPracticeInfo, ctx: AgentRunContext): void {
  ctx.setPendingAction(null);
  const timeRange = `${info.startTime}–${info.endTime}`;
  ctx.addMessage(
    'assistant',
    `I've got a placeholder set up for **${info.date}** from ${timeRange} at **${info.location}** (theme: **${info.theme}**). What would you like to focus on for this practice — any specific drills or areas?`
  );
  ctx.setPendingPracticeForFocus(info);
}

/**
 * Kick off the placeholder practice flow. If the user already mentioned
 * a focus, generate a plan immediately; otherwise ask what to focus on.
 */
export async function startPracticePlaceholderFlow(
  ctx: AgentRunContext,
  userMessage?: string
): Promise<void> {
  const extractedFocus = userMessage ? extractPracticeFocusFromMessage(userMessage) : null;
  ctx.addMessage(
    'assistant',
    extractedFocus
      ? `Sure, I'll create a practice and build the plan around **${extractedFocus}**.`
      : 'Sure, let me create a placeholder for your practice.'
  );
  ctx.setIsLoading(true);
  try {
    const info = await createPlaceholderPractice(ctx.team);
    if (!info) {
      ctx.addMessage(
        'assistant',
        "I wasn't able to create the practice. Try again or set one up from the Practices page."
      );
      ctx.setIsLoading(false);
      return;
    }
    toast.success('Placeholder practice created.');

    if (extractedFocus) {
      ctx.setGenerationProgress('Preparing your practice plan…');
      try {
        await runPlanGeneration(info, extractedFocus, ctx.setGenerationProgress);
        ctx.setGenerationProgress(null);
        toast.success('Practice plan generated.');
        ctx.addMessage(
          'assistant',
          `I've built your practice around **${extractedFocus}**. Here’s your practice.`,
          { label: `Practice ${info.date}`, href: '/practices' }
        );
      } catch (err) {
        ctx.setGenerationProgress(null);
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to generate practice plan');
        ctx.addMessage(
          'assistant',
          'Something went wrong generating the plan. I’ve still got your placeholder — tell me what you’d like to focus on and I’ll try again.'
        );
        handlePracticeCreated(info, ctx);
      } finally {
        ctx.setIsLoading(false);
      }
      return;
    }

    handlePracticeCreated(info, ctx);
  } catch (err) {
    console.error(err);
    ctx.addMessage(
      'assistant',
      "Something went wrong creating the placeholder. You can create a practice from the Practices page."
    );
  } finally {
    ctx.setIsLoading(false);
  }
}

/**
 * Continue an in-flight "what would you like to focus on?" practice.
 * Called from the chat handler when `pendingPracticeForFocus` is set.
 */
export async function continuePendingPracticeFocus(
  text: string,
  info: CreatedPracticeInfo,
  ctx: AgentRunContext
): Promise<void> {
  ctx.setPendingPracticeForFocus(null);
  ctx.setIsLoading(true);
  ctx.setGenerationProgress('Preparing your practice plan…');
  try {
    await runPlanGeneration(info, text, ctx.setGenerationProgress);
    ctx.setGenerationProgress(null);
    toast.success('Practice plan generated.');
    ctx.addMessage(
      'assistant',
      `I’ve built your practice plan around what you said. Here’s your practice.`,
      { label: `Practice ${info.date}`, href: '/practices' }
    );
  } catch (err) {
    ctx.setGenerationProgress(null);
    console.error(err);
    toast.error(err instanceof Error ? err.message : 'Failed to generate practice plan');
    ctx.addMessage(
      'assistant',
      'Something went wrong generating the plan. You can try again from the Practices page, or tell me another focus and I’ll try again.'
    );
    ctx.setPendingPracticeForFocus(info);
  } finally {
    ctx.setIsLoading(false);
  }
}

async function runAddDrillForResolvedDate(
  iso: string,
  drillPhrase: string,
  ctx: AgentRunContext,
  rawUserMessage?: string
): Promise<void> {
  if (!ctx.checkFeatureAccess('AI-powered practice planning')) {
    ctx.addMessage(
      'assistant',
      "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more."
    );
    return;
  }

  const practice = await findFirstPracticeOnDate(ctx.team.id, iso);

  if (!practice) {
    ctx.addMessage(
      'assistant',
      `There isn’t a practice scheduled for **${formatPracticeDateLabel(iso)}** yet.\n\nIf you’d like, I can **create one** using your usual time and location and build a practice plan that **includes ${drillPhrase}**.\nReply **yes** or **no**.`
    );
    ctx.setPendingAddDrillFollowUp({
      phase: 'awaitCreateConfirm',
      targetDateISO: iso,
      drillDescription: drillPhrase,
    });
    return;
  }

  ctx.setIsLoading(true);
  ctx.setGenerationProgress(`Adding **${drillPhrase}**…`);
  try {
    const durationMinutes = inferActivityDurationMinutes(
      `${drillPhrase} ${rawUserMessage ?? ''}`.trim(),
      12
    );
    const expanded = (await generateExpandedDrillForPractice(
      ctx.team,
      drillPhrase,
      durationMinutes
    )) as RawAiDrill;
    const [row] = drillsFromAiShape(practice.id, [expanded]);
    const meta = await listDrillsOrderMeta(practice.id);
    const names = meta.map((m) => m.name);
    let ix = computeDrillInsertVisualIndex(names, drillPhrase.toLowerCase());
    if (/\b(last|final)\s+\d+\s*(?:min|minutes?)/i.test(drillPhrase)) {
      const cool = names.findIndex((n) => n.toLowerCase().includes('cool'));
      ix = cool === -1 ? names.length : Math.max(0, cool);
    }
    await insertPracticeDrillAtOrderSlot(practice.id, row, ix);
    toast.success('Drill added.');
    ctx.addMessage(
      'assistant',
      `I added **${row.name || drillPhrase}** to your **${formatPracticeDateLabel(iso)}** practice and placed it in a logical spot on the timeline.`,
      { label: 'Open practices', href: '/practices' }
    );
  } catch (err) {
    console.error(err);
    toast.error(err instanceof Error ? err.message : 'Failed to add drill');
    ctx.addMessage(
      'assistant',
      'I couldn’t add that drill automatically. You can add it from the Practices page, or describe the drill again and we can retry.'
    );
  } finally {
    ctx.setGenerationProgress(null);
    ctx.setIsLoading(false);
  }
}

async function createPracticeWithMandatoryDrill(
  iso: string,
  drillPhrase: string,
  ctx: AgentRunContext
): Promise<void> {
  if (!ctx.checkFeatureAccess('AI-powered practice planning')) {
    ctx.addMessage(
      'assistant',
      "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more."
    );
    return;
  }

  ctx.setIsLoading(true);
  ctx.setGenerationProgress('Creating practice…');
  try {
    const info = await createPlaceholderPractice(ctx.team, iso);
    if (!info) {
      toast.error('Could not create practice');
      ctx.addMessage(
        'assistant',
        "I wasn’t able to create the practice. Try the Practices page, then ask me again to add the drill."
      );
      return;
    }
    toast.success('Practice created.');

    const focus = `${drillPhrase} — feature this throughout one focused segment while keeping the rest of the session balanced with fundamentals, teamwork, hydration, and a cooldown.`;

    ctx.setGenerationProgress('Generating your practice plan…');
    await runPlanGeneration(info, focus, ctx.setGenerationProgress, {
      coachMandatoryDrills: [drillPhrase],
    });
    ctx.setGenerationProgress(null);
    toast.success('Practice plan generated.');
    ctx.addMessage(
      'assistant',
      `I scheduled **${formatPracticeDateLabel(iso)}** and built a practice plan that **includes ${drillPhrase}**.`,
      { label: `Practice ${info.date}`, href: '/practices' }
    );
  } catch (err) {
    ctx.setGenerationProgress(null);
    console.error(err);
    toast.error(err instanceof Error ? err.message : 'Failed to generate plan');
    ctx.addMessage(
      'assistant',
      'Something went wrong generating the plan. The practice is still on your calendar — pick a focus from Practices or ask me to try generating again.'
    );
  } finally {
    ctx.setIsLoading(false);
  }
}

async function handleAddDrillIntentMessage(text: string, ctx: AgentRunContext): Promise<AgentRunResult> {
  if (!ctx.checkFeatureAccess('AI-powered practice planning')) {
    ctx.addMessage(
      'assistant',
      "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more."
    );
    return { kind: 'handled' };
  }

  const drillPhrase = extractPracticeActivityBrief(text);
  if (!drillPhrase) {
    ctx.addMessage(
      'assistant',
      'I couldn’t quite tell **what** to add (drill name, activity, etc.). Example: **Add a first baseman pickoff drill to tomorrow’s practice** or **Can you add a fun team game during the last 20 minutes of practice?**'
    );
    return { kind: 'handled' };
  }

  const targetDate = parsePracticeTargetDateFromMessage(text);
  if (!targetDate) {
    ctx.setPendingAddDrillFollowUp({ phase: 'needDate', drillDescription: drillPhrase });
    ctx.addMessage(
      'assistant',
      `Got it — **${drillPhrase}**. Which practice day should I use? (**tomorrow**, a weekday like **Tuesday**, or **YYYY-MM-DD**.)`
    );
    return { kind: 'handled' };
  }

  await runAddDrillForResolvedDate(targetDate, drillPhrase, ctx, text);
  return { kind: 'handled' };
}

/** Continue pending “which date?” or “create practice?” replies for add-drill flow. */
export async function continuePendingAddDrillFollowUp(text: string, ctx: AgentRunContext): Promise<void> {
  const follow = ctx.pendingAddDrillFollowUp;
  if (!follow) return;

  if (follow.phase === 'needDate') {
    const iso = parsePracticeTargetDateFromMessage(text);
    if (!iso) {
      ctx.addMessage(
        'assistant',
        `I still need the practice date for **${follow.drillDescription}**. Try **tomorrow**, a weekday, or **YYYY-MM-DD**.`
      );
      return;
    }
    ctx.setPendingAddDrillFollowUp(null);
    await runAddDrillForResolvedDate(iso, follow.drillDescription, ctx, text);
    return;
  }

  if (follow.phase === 'awaitCreateConfirm') {
    if (isAffirmativeReply(text)) {
      ctx.setPendingAddDrillFollowUp(null);
      await createPracticeWithMandatoryDrill(follow.targetDateISO, follow.drillDescription, ctx);
      return;
    }
    if (isNegativeReply(text)) {
      ctx.setPendingAddDrillFollowUp(null);
      ctx.addMessage(
        'assistant',
        'All right — I didn’t create a practice. If you schedule one later, ask me again and I’ll add that drill.'
      );
      return;
    }
    ctx.addMessage(
      'assistant',
      'Reply **yes** if you want me to create that practice with a generated plan including your drill, or **no** to cancel.'
    );
  }
}

/** Top-level practice agent invoked when the regex dispatcher matches. */
export const practiceAgent: CoachSteveAgent = {
  name: 'practice',
  description:
    'Creates placeholder practices plus AI-generated plans; adds a requested drill onto a dated practice or creates one so the drill is included.',
  applicableSports: 'all',
  match: (text) => isCreatePracticeIntent(text) || isAddDrillToPracticeIntent(text),
  async handle(text, ctx): Promise<AgentRunResult> {
    if (isAddDrillToPracticeIntent(text)) {
      await handleAddDrillIntentMessage(text, ctx);
      return { kind: 'handled' };
    }
    if (!ctx.checkFeatureAccess('AI-powered practice planning')) {
      ctx.addMessage(
        'assistant',
        "AI-powered practice planning is a Pro feature. Upgrade to Pro for unlimited AI Coach, practice & game plans, lineup optimization, and more."
      );
      return { kind: 'handled' };
    }
    await startPracticePlaceholderFlow(ctx, text);
    return { kind: 'handled' };
  },
};
